import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { BlankSlate } from '@factorialco/deprecated-design-system'
import {
  CreatePayrollRunCommentDocument,
  CreatePayrollRunCommentMutation,
  PayrollRunCommentsDocument,
} from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import MessagesCallout from 'modules/bookkeepers/components/PayrollCycle/Messages/MessagesCallout'
import { CommentsCommentValues } from 'modules/comments/components/CommentsContainer/CommentEditor'
import SidepanelComments from 'modules/comments/components/Wrappers/SidepanelComments'
import { usePayrollRunParticipants } from 'modules/compensations/hooks/usePayrollRunParticipants'

interface CommentsProps {
  payrollRunId: number
}

const Comments = ({ payrollRunId }: CommentsProps) => {
  const { company } = useCurrentSession()

  const { data: commentsData, error } = useQuery(PayrollRunCommentsDocument, {
    variables: { companyId: company.id, id: payrollRunId },
  })

  const comments =
    commentsData?.compensations?.payrollRunsConnection?.nodes?.[0]?.commentsConnection?.nodes

  const participantAccessIds =
    commentsData?.compensations?.payrollRunsConnection?.nodes?.[0]?.payrollRunParticipants ?? []

  const { fetchMentionableAccesses } = usePayrollRunParticipants({ participantAccessIds })

  const [addComment] = useMutation(CreatePayrollRunCommentDocument, {
    extractorFn: (data: CreatePayrollRunCommentMutation) =>
      data.compensationsMutations.createCommentCompensationsPayrollRun,
  })

  const handleAddComment = (variables: CommentsCommentValues) => {
    return addComment({
      variables: { ...variables, payrollRunId },
      refetchQueries: [PayrollRunCommentsDocument],
      awaitRefetchQueries: true,
    })
  }

  return (
    <ErrorBoundary>
      {error ? (
        <Box alignItems="center" justifyContent="center" height="full">
          <BlankSlate
            illustration="alert"
            title={i18n.t('common.crash.title')}
            description={i18n.t('common.crash.retry')}
          />
        </Box>
      ) : !commentsData && !error ? (
        <Box alignItems="center" justifyContent="center" height="full" padding="s48">
          <Spinner />
        </Box>
      ) : (
        comments && (
          <Box padding="s24">
            <MessagesCallout
              isBookkeeperView={false}
              payrollRunId={payrollRunId}
              hasMessages={comments.length > 0}
            />
            <SidepanelComments
              comments={comments}
              onAddComment={handleAddComment}
              resourceId={payrollRunId}
              onFetchCustomMentionableAccesses={fetchMentionableAccesses}
            />
          </Box>
        )
      )}
    </ErrorBoundary>
  )
}

export default Comments
